import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listEvents } from "@/lib/events";
import EventCard from "@/components/EventCard";
import { ArrowRight, Plus, Sparkles } from "lucide-react";
import { coverFor } from "@/lib/defaultEventImages";
import { CATEGORIES as INONIT_CATEGORIES } from "@/lib/categories";

const CATEGORIES = INONIT_CATEGORIES.map((c) => ({ key: c.key, emoji: c.emoji }));

const STEPS = [
  { n: "01", title: "See what's near you.", body: "A quiet, chronological feed of real events. No algorithm. No noise." },
  { n: "02", title: "Post in seconds.",     body: "A coffee, a hike, a movie night. If you'd show up, someone else will too." },
  { n: "03", title: "Actually show up.",    body: "Tap join, walk over, say hi. The whole thing only works offline." },
];

export default function HomePage() {
  const [previewEvents, setPreviewEvents] = useState([]);

  useEffect(() => {
    const ctrl = new AbortController();
    listEvents({ limit: 6, signal: ctrl.signal })
      .then(setPreviewEvents)
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  return (
    <div className="bg-paper text-ink">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-12 md:pb-24">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-end">
          <div>
            <span className="inline-flex items-center gap-2 bg-paper border-[2.5px] border-ink rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.25em] shadow-pop-sm">
              <Sparkles size={12} strokeWidth={2.5} className="text-ink" />
              New · be where the people are
            </span>
            <h1 className="font-display text-[3.1rem] sm:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tight mt-6">
              say <span className="inline-block bg-lime border-[3px] border-ink rounded-2xl px-3 -rotate-2">yes</span><br />
              to a&nbsp;thing<br />
              this <span className="underline decoration-[6px] decoration-ink underline-offset-[10px]">week</span>.
            </h1>
            <p className="text-ink/70 text-lg md:text-xl max-w-xl mt-7 leading-relaxed">
              inonit is a quiet board for things happening near you — hikes, dinners, jam nights, book clubs. Post one. Show up.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/feed" data-testid="home-cta-feed" className="cta-primary">
                See what's on <ArrowRight size={16} strokeWidth={2.8} />
              </Link>
              <Link to="/create" data-testid="home-cta-create" className="cta-ghost">
                <Plus size={16} strokeWidth={2.8} /> Post a thing
              </Link>
            </div>
          </div>

          {/* Stacked preview cards */}
          <div className="relative hidden lg:block min-h-[440px]">
            {previewEvents.slice(0, 3).map((e, i) => (
              <div
                key={e.id}
                className="absolute right-0 w-[90%] transform origin-top-right pop"
                style={{
                  top: `${i * 24}px`,
                  right: `${i * 14}px`,
                  transform: `rotate(${(i - 1) * 1.6}deg)`,
                  zIndex: 10 - i,
                }}
              >
                <PreviewCard event={e} />
              </div>
            ))}
            {previewEvents.length === 0 && (
              <div className="absolute inset-0 pop bg-paper flex items-center justify-center font-display text-2xl text-ink/40">
                Loading the feed…
              </div>
            )}
          </div>
        </div>

        {/* Category strip */}
        <div className="mt-12 md:mt-16 flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 md:mx-0 md:px-0">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/feed?category=${c.key}`}
              className="shrink-0 inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-widest bg-paper hover:bg-ink hover:text-paper transition-colors"
            >
              <span aria-hidden>{c.emoji}</span>{c.key}
            </Link>
          ))}
          <Link
            to="/feed"
            className="shrink-0 inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-widest bg-lime hover:translate-x-[-1px] hover:translate-y-[-1px] transition-transform shadow-pop-sm"
          >
            All <ArrowRight size={14} strokeWidth={2.8} />
          </Link>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-ink text-paper">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <h2 className="font-display text-4xl md:text-6xl text-paper leading-[0.95] tracking-tight mb-12">
            three taps. <span className="text-lime">that's it.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-10">
            {STEPS.map((s) => (
              <div key={s.n} className="border-[3px] border-paper/15 rounded-3xl p-7">
                <span className="font-display text-5xl text-lime">{s.n}</span>
                <h3 className="font-display text-2xl md:text-3xl text-paper mt-4 mb-3 leading-tight">{s.title}</h3>
                <p className="text-paper/65 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PREVIEW */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <h2 className="font-display text-4xl md:text-6xl text-ink leading-[0.95] tracking-tight">
            on the board <span className="text-ink/35">today.</span>
          </h2>
          <Link to="/feed" data-testid="home-see-all" className="cta-ghost">
            See all <ArrowRight size={16} strokeWidth={2.8} />
          </Link>
        </div>
        {previewEvents.length === 0 ? (
          <div className="pop p-10 text-center font-display text-2xl text-ink/40">
            No events yet — be the first to post.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {previewEvents.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        )}
      </section>

      {/* CLOSER */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20">
        <div className="pop bg-lime p-8 md:p-12 text-center">
          <h2 className="font-display text-4xl md:text-6xl text-ink leading-[0.95] tracking-tight mb-3">
            see you out there.
          </h2>
          <p className="text-ink/75 max-w-lg mx-auto mb-7">
            One small thing this week. That's the whole brief.
          </p>
          <Link to="/create" data-testid="home-cta-bottom" className="cta-ghost">
            <Plus size={16} strokeWidth={2.8} /> Post your first event
          </Link>
        </div>
      </section>
    </div>
  );
}

function PreviewCard({ event }) {
  return (
    <div className="relative">
      <div className="w-full aspect-[16/10] overflow-hidden bg-[#EDE7D2] rounded-t-[1.4rem] border-b-[3px] border-ink">
        <img src={coverFor(event)} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <span className="inline-block bg-lime border-[2.5px] border-ink rounded-full px-3 py-0.5 text-[10px] font-extrabold uppercase tracking-widest mb-2">
          {event.category}
        </span>
        <h3 className="font-display text-2xl leading-tight">{event.title}</h3>
        <p className="text-ink/60 text-sm mt-1">{event.city}</p>
      </div>
    </div>
  );
}
